import { fade, hex, type Color } from "../../common/color.js";
import { insets, type Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import { BaseTheme, derivePalette, makeMetrics } from "./BaseTheme.js";
import { drawCornerTicks, drawGlow } from "./glyphs.js";
import type {
  CaptionButton,
  GraphNodeVisual,
  ItemSlotVisual,
  RadialWedgeVisual,
  ScrollBarParts,
  StatBarVisual,
  VisualState,
  WindowVisualState,
} from "./Theme.js";

const HUD_FONT = "'Bahnschrift', 'DIN Alternate', 'Segoe UI', system-ui, sans-serif";
const HUD_MONO = "'Cascadia Mono', Consolas, monospace";

const CYAN = hex("#38e0f0");
/** The warning color beside the cyan: a low bar, a live corner tick. */
const AMBER = hex("#f0b038");
/** Draw-path colors, parsed once rather than on every call. */
const DEEP_TEAL = hex("#04141a");

/**
 * "Nebula" — a heads-up-display look: near-black glass, hairline cyan edges,
 * bracket ticks at every corner and a soft glow on anything active.
 */
export class SciFiTheme extends BaseTheme {
  constructor() {
    super({
      name: "sci-fi",
      isDark: true,
      metrics: makeMetrics({
        titleBarHeight: 30,
        windowCornerRadius: 2,
        controlCornerRadius: 2,
        controlHeight: 28,
        captionButtonWidth: 40,
        focusRingWidth: 1,
        focusRingInset: -3,
        checkBoxSize: 16,
        radioSize: 16,
        scrollBarSize: 12,
        listItemHeight: 24,
        headerHeight: 28,
        font: { family: HUD_FONT, size: 13 },
        captionFont: { family: HUD_FONT, size: 13, weight: 600 },
        monoFont: { family: HUD_MONO, size: 12.5 },
      }),
      palette: derivePalette({
        desktop: hex("#03080e"),
        surface: hex("#081521"),
        raised: hex("#0e2534"),
        field: hex("#04101a"),
        ink: hex("#cdeef7"),
        accent: CYAN,
        accentText: hex("#02171c"),
        border: hex("#1c4a5c"),
        titleBar: hex("#0a1f2e"),
        titleText: CYAN,
        isDark: true,
        overrides: {
          windowBorderActive: fade(CYAN, 0.75),
          windowBorderInactive: hex("#173a49"),
          textSubtle: hex("#6f9fb0"),
          scrollTrack: hex("#04101a"),
          scrollThumb: fade(CYAN, 0.35),
          scrollThumbHover: fade(CYAN, 0.55),
          scrollThumbPressed: fade(CYAN, 0.8),
          headerBg: hex("#0b1f2c"),
          headerText: CYAN,
          gridLine: hex("#13323f"),
          selectionBg: fade(CYAN, 0.28),
          selectionText: hex("#e6feff"),
          selectionInactiveBg: hex("#12303c"),
        },
      }),
    });
  }

  protected override get focusUnderline(): boolean {
    return true;
  }

  /** Glow under the active frame, plus brackets at the corners. */
  protected override decorateWindow(rc: RenderContext, bounds: Rect, s: WindowVisualState): void {
    const p = this.palette;
    const b = this.metrics.windowBorder;
    // A bright rule under the title bar reads as the HUD's separator.
    rc.fillRect(
      { x: b, y: b + this.captionHeight(s) - 1, width: bounds.width - b * 2, height: 1 },
      s.active ? fade(CYAN, 0.8) : fade(CYAN, 0.25),
    );
    if (s.active) {
      drawCornerTicks(
        rc,
        { x: 0, y: 0, width: bounds.width, height: bounds.height },
        fade(CYAN, 0.9),
        14,
        1,
        2,
      );
    }
  }

  protected override decorateButton(rc: RenderContext, r: Rect, s: VisualState): void {
    if (s.disabled) return;
    // Default buttons carry the glow; the rest light up only under the pointer.
    if (s.isDefault === true || s.hovered || s.pressed) {
      drawGlow(rc, r, CYAN, this.controlRadius, s.isDefault === true ? 5 : 3);
    }
    // Two short accent bars mark the leading and trailing edge.
    const bar = fade(CYAN, s.isDefault === true ? 0.9 : 0.45);
    const h = Math.max(2, Math.min(10, r.height - 10));
    const y = r.y + (r.height - h) / 2;
    rc.fillRect({ x: r.x + 2, y, width: 2, height: h }, bar);
    rc.fillRect({ x: r.x + r.width - 4, y, width: 2, height: h }, bar);
  }

  protected override captionButtonHighlight(button: CaptionButton, pressed: boolean): Color {
    if (button === "close") return pressed ? hex("#c0392b") : hex("#e8503f");
    return pressed ? fade(CYAN, 0.35) : fade(CYAN, 0.2);
  }

  protected override closeGlyphInk(): Color {
    return hex("#ffffff");
  }

  /** A squared-off thumb with a track rule, rather than Fluent's soft pill. */
  override drawScrollBar(rc: RenderContext, parts: ScrollBarParts, s: VisualState): void {
    const p = this.palette;
    rc.fillRect(parts.bounds, p.scrollTrack);
    if (parts.vertical) {
      rc.vLine(parts.bounds.x + parts.bounds.width / 2, parts.bounds.y, parts.bounds.height, p.gridLine);
    } else {
      rc.hLine(parts.bounds.x, parts.bounds.y + parts.bounds.height / 2, parts.bounds.width, p.gridLine);
    }
    if (parts.thumb.width <= 0 || parts.thumb.height <= 0) return;

    const hovered = parts.hoveredPart === "thumb" || s.hovered;
    const pressed = parts.pressedPart === "thumb";
    const color = pressed ? p.scrollThumbPressed : hovered ? p.scrollThumbHover : p.scrollThumb;
    rc.drawRect(parts.thumb, { fill: color, borderWidth: 1, borderColor: fade(CYAN, 0.7) });
  }

  // --- game chrome --------------------------------------------------------

  override drawStatBarFrame(rc: RenderContext, r: Rect, s: StatBarVisual): void {
    // A lit channel: brackets at each end and a hairline down the middle,
    // rather than a box. A HUD reads as a readout, not as a widget.
    rc.fillRect(r, fade(CYAN, 0.08), 0);
    rc.strokeRect(r, fade(s.low && !s.disabled ? AMBER : CYAN, 0.85), 1, 0);
    const tick = Math.min(6, r.height);
    rc.vLine(r.x + 2, r.y + (r.height - tick) / 2, tick, fade(CYAN, 0.9));
    rc.vLine(r.x + r.width - 3, r.y + (r.height - tick) / 2, tick, fade(CYAN, 0.9));
  }

  override drawItemSlot(rc: RenderContext, r: Rect, s: ItemSlotVisual): void {
    rc.fillRect(r, fade(CYAN, s.locked ? 0.03 : 0.09), 0);
    const edge = s.highlighted ? AMBER : s.rarity ?? fade(CYAN, 0.75);
    rc.strokeRect(r, edge, 1, 0);
    // Corner ticks instead of a full border on hover: the console look.
    if (!s.hovered || s.locked) return;
    const n = 5;
    rc.hLine(r.x, r.y, n, AMBER);
    rc.vLine(r.x, r.y, n, AMBER);
    rc.hLine(r.x + r.width - n, r.y + r.height - 1, n, AMBER);
    rc.vLine(r.x + r.width - 1, r.y + r.height - n, n, AMBER);
  }

  override drawDialogueBox(rc: RenderContext, r: Rect): void {
    rc.fillRect(r, fade(DEEP_TEAL, 0.9), 0);
    rc.strokeRect(r, fade(CYAN, 0.8), 1, 0);
    // A lit rail along the top edge: transmission incoming.
    rc.hLine(r.x + 8, r.y + 3, Math.max(0, r.width - 16), fade(AMBER, 0.7));
  }

  override drawRadialWedge(rc: RenderContext, w: RadialWedgeVisual): void {
    // A lit segment rather than a filled pie: the wheel reads as a readout.
    const fill = w.disabled
      ? fade(CYAN, 0.04)
      : w.highlighted
        ? fade(AMBER, 0.32)
        : fade(w.color ?? CYAN, 0.12);
    rc.fillArc(w.center, w.innerRadius, w.outerRadius, w.startAngle, w.endAngle, fill);
    // A bright rim along the outer edge, which is where a HUD puts its weight.
    const edge = w.disabled ? fade(CYAN, 0.2) : w.highlighted ? AMBER : fade(CYAN, 0.7);
    rc.fillArc(w.center, w.outerRadius - 1.5, w.outerRadius, w.startAngle, w.endAngle, edge);
  }

  override drawGraphNode(rc: RenderContext, n: GraphNodeVisual): void {
    const base = n.color ?? CYAN;
    const fill = n.locked
      ? fade(CYAN, 0.04)
      : n.taken
        ? fade(base, 0.35)
        : n.partial
          ? fade(base, 0.18)
          : fade(CYAN, 0.08);
    rc.fillArc(n.center, 0, n.radius, 0, Math.PI * 2, fill);
    rc.fillArc(
      n.center,
      n.radius - 1.5,
      n.radius,
      0,
      Math.PI * 2,
      n.locked ? fade(CYAN, 0.25) : n.hovered ? AMBER : fade(base, 0.85),
    );
  }

  override drawNameplateFrame(rc: RenderContext, r: Rect): void {
    // A scan bar under the name, the way a targeting reticle labels a contact.
    rc.fillRect({ x: r.x, y: r.y, width: r.width, height: r.height }, fade(DEEP_TEAL, 0.55), 0);
    rc.hLine(r.x, r.y, r.width, fade(CYAN, 0.7));
  }

  override drawResourceBackground(rc: RenderContext, r: Rect): void {
    rc.fillRect(r, fade(DEEP_TEAL, 0.6), 0);
    rc.hLine(r.x, r.y + r.height - 1, r.width, fade(CYAN, 0.5));
  }

  override drawColumnHeader(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    rc.fillRect(r, s.pressed ? p.controlBgPressed : s.hovered ? p.headerBgHover : p.headerBg);
    rc.hLine(r.x, r.y + r.height - 1, r.width, fade(CYAN, 0.6));
    rc.vLine(r.x + r.width - 1, r.y + 4, r.height - 8, p.gridLine);
  }
}

/** Metrics reuse for anything that wants the HUD font without the chrome. */
export const SCIFI_METRICS = makeMetrics({
  font: { family: HUD_FONT, size: 13 },
  buttonPadding: insets(14, 6),
});
